import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledTextField, ControlledCheckbox, ControlNumberField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimContractData, BimContractObjectData } from 'gql/graphql';
import { IconAlert, Informer, Text, cnMixSpace } from 'uikit';
import { sortByYear } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import cn from 'classnames';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { ItemTableContractObject } from '../BimContractStructure/hooks/useBimItemsContractStructureTable';
import { useBimUniqueContractObject } from '../BimContractStructure/hooks/useBimUniqueContractObject';

import { useBimContractObject } from './useBimContractObject';

import Styles from './BimCreateStructureObject.module.css';

interface ICreateContractObjectProps {
    contractId: number;
    onClose: () => void;
    onSuccess: (item: BimContractObjectData) => void;
    constructionTypeId?: number;
    contractObject?: ItemTableContractObject;
    seasons?: BimContractData['seasons'];
    isContractTypeNormal?: boolean;
    isUseContract?: boolean;
    isUseWorkOrder?: boolean;
    refreshProject: () => void;
    bimContractObjectsMakeRequest: () => void;
}

export type FieldValuesBimCreateStructure = Record<string, string | boolean> & {
    objectName: string;
    hasGuaranteedVolume: boolean;
};

const rules = {
    validate: (value: any) => (!value ? 'Обязательное поле' : true),
};

export const BimCreateStructureObject: React.FC<ICreateContractObjectProps> = (props: ICreateContractObjectProps) => {
    const {
        contractId,
        contractObject,
        constructionTypeId,
        seasons,
        isContractTypeNormal,
        isUseContract,
        onClose,
        onSuccess,
        refreshProject,
        bimContractObjectsMakeRequest,
    } = props;
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);

    const { defaultValuesStructureContract, onSubmitStructureContract } = useBimContractObject({
        isUseContract,
        contractObject,
        contractId,
        constructionTypeId,
        seasons,
        onClose,
        onSuccess,
        refreshProject,
        bimContractObjectsMakeRequest,
    });

    const { control, formState, handleSubmit, watch, reset, setValue, setError, clearErrors } =
        useForm<FieldValuesBimCreateStructure>({
            defaultValues: defaultValuesStructureContract,
        });

    const watchAllFields = watch();
    const isDirtyObjectName = formState?.dirtyFields?.objectName;

    const { bimUniqueContractObjectData, bimUniqueContractObjectStatus, resetBimIsUniqueContractObjectName } =
        useBimUniqueContractObject({
            contractId,
            constructionTypeId,
            name: watchAllFields.objectName,
            isDirtyObjectName,
        });

    const isOneHundredPercent = useMemo(() => {
        const sumPercent = Object.entries(watchAllFields)
            .reduce((acc: number[], [fieldName, value]) => {
                if (value && fieldName.includes('season_')) {
                    acc = [...acc, replacerCost(value as string)];
                }

                return acc;
            }, [])
            .reduce((a, b) => a + b, 0);

        return sumPercent === 100;
    }, [watchAllFields]);

    const onSubmitDialog = useCallback(
        (formData) => {
            if (isUseContract) {
                onSubmitStructureContract(formData);
            }
        },
        [isUseContract, onSubmitStructureContract],
    );

    const onCloseDialog = useCallback(() => {
        if (formState?.isDirty) {
            setIsOpenWarningDialog(true);
        } else {
            onClose();
        }
    }, [formState?.isDirty, onClose]);

    const onSubmitWarningDialog = useCallback(() => {
        onClose();
    }, [onClose]);

    const onCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const setErrorSeasons = useCallback(() => {
        seasons?.forEach((season) => setError(`season_${season.year}`, { message: undefined }));
    }, [seasons, setError]);

    const clearErrorsSeasons = useCallback(() => {
        seasons?.forEach((season) => clearErrors(`season_${season.year}`));
    }, [clearErrors, seasons]);

    useEffect(() => {
        if (formState?.isDirty && !isOneHundredPercent) {
            setErrorSeasons();
        } else {
            clearErrorsSeasons();
        }
    }, [clearErrorsSeasons, formState?.isDirty, isOneHundredPercent, setErrorSeasons]);

    const uniqueContractResult = useMemo(() => {
        const isEqualEntered = defaultValuesStructureContract?.objectName === watchAllFields?.objectName?.trim();

        return isEqualEntered ? false : !bimUniqueContractObjectData?.result;
    }, [bimUniqueContractObjectData?.result, defaultValuesStructureContract?.objectName, watchAllFields?.objectName]);

    useEffect(() => {
        if (
            watchAllFields.objectName &&
            bimUniqueContractObjectStatus === RequestStatus.SUCCESS &&
            uniqueContractResult
        ) {
            setError('objectName', { message: 'Объект с таким наименованием уже существует' });
        } else {
            clearErrors('objectName');
        }
    }, [bimUniqueContractObjectStatus, clearErrors, uniqueContractResult, setError, watchAllFields.objectName]);

    useEffect(() => {
        if (!defaultValuesStructureContract) {
            const seasonsValueZero = seasons?.reduce((acc: Record<string, string>, season) => {
                acc = { ...acc, [`season_${season.year}`]: '0,00' };

                return acc;
            }, {});

            reset({ ...seasonsValueZero, hasGuaranteedVolume: isContractTypeNormal });

            setErrorSeasons();
        } else if (isContractTypeNormal) {
            reset({ hasGuaranteedVolume: isContractTypeNormal });
        }

        const subscription = watch((data, { name }) => {
            if (name?.includes('season') && !data[name]) {
                setValue(name, '0,00');
            }

            return data;
        });

        return () => subscription.unsubscribe();
    }, [defaultValuesStructureContract, isContractTypeNormal, reset, seasons, setErrorSeasons, setValue, watch]);

    useEffect(
        () => () => {
            resetBimIsUniqueContractObjectName(null);
        },
        [resetBimIsUniqueContractObjectName],
    );

    const btnConfirmTitle = `${defaultValuesStructureContract ? 'Редактирование' : 'Создание'} объекта`;
    const btnConfirmLabel = defaultValuesStructureContract ? 'Сохранить' : 'Создать';
    const btnConfirmDisabled =
        !formState?.isDirty || !isOneHundredPercent || !watchAllFields.objectName || !R.isEmpty(formState.errors);

    return (
        <ModalDialog
            title={btnConfirmTitle}
            isOpen
            onClose={onCloseDialog}
            onConfirm={handleSubmit(onSubmitDialog)}
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={btnConfirmDisabled}
            className={Styles.modal}
            withTitleCloseButton
        >
            <div className={cnMixSpace({ m: 'xl' })}>
                {!isOneHundredPercent && (
                    <Informer
                        view="filled"
                        status="alert"
                        label="Распределение итогов суммарно должно быть 100%"
                        className={cnMixSpace({ mB: 'm' })}
                        icon={IconAlert}
                        size="s"
                    />
                )}
                <form>
                    <ControlledTextField
                        name="objectName"
                        label="Наименование"
                        placeholder="Наименование объекта"
                        control={control}
                        type="text"
                        view="default"
                        labelPosition="top"
                        className={cnMixSpace({ mB: 'm' })}
                        required
                        rules={rules}
                    />
                    <ControlledCheckbox
                        className={`${cnMixSpace({ mB: 'm' })} ${Styles.hasGuaranteedVolume}`}
                        name="hasGuaranteedVolume"
                        label="Признак гарантированного объема"
                        size="l"
                        control={control}
                        disabled={isContractTypeNormal}
                        style={{ fontSize: 14 }}
                    />
                    <div className={Styles.wrapper_percentage}>
                        <Text view="secondary" className={cn(Styles.title_percentage, cnMixSpace({ pB: 'm' }))}>
                            Распределение итогов по сезонам
                            <span className="FieldLabel-Star">*</span>
                        </Text>
                        {[...(seasons ?? [])].sort(sortByYear).map((season, i) => (
                            <ControlNumberField
                                key={`season_${season.year}`}
                                name={`season_${season.year}`}
                                label={season.year}
                                control={control}
                                labelPosition="left"
                                size="s"
                                width="full"
                                rightSide="%"
                                className={cn({ [cnMixSpace({ mT: 'xs' })]: i !== 0 })}
                                rules={rules}
                            />
                        ))}
                    </div>
                </form>
            </div>
            <ConfirmationDialog
                textHeader="ВВОД ДАННЫХ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenWarningDialog}
                onSubmit={onSubmitWarningDialog}
                onClose={onCancelWarningDialog}
            />
        </ModalDialog>
    );
};

BimCreateStructureObject.displayName = 'BimCreateStructureObject';
